import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import type {
  ChannelReceipt,
  Message,
  MessageReply,
  PublicUser,
  Reaction,
} from '@orbit-sync/shared-types';
import { useAuthStore } from '../auth/authStore.js';
import {
  EMPTY_RECEIPTS,
  deliveryStatus,
  useChatStore,
  type PendingMessage,
  type Transcript,
} from '../chat/chatStore.js';
import { dirFor } from '../chat/direction.js';
import { useDirectory } from '../chat/directoryStore.js';
import { formatDay, formatTime, memberLabel, sameDay } from '../chat/labels.js';
import { copyText } from '../lib/clipboard.js';
import { useSettingsStore } from '../settings/settingsStore.js';
import { useSocket } from '../socket/SocketProvider.js';
import { Avatar } from './Avatar.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { EmptyState } from './EmptyState.js';
import { FileCard } from './FileUploads.js';
import { ForwardDialog } from './ForwardDialog.js';
import { Icon } from './Icon.js';
import { MessageEditor } from './MessageEditor.js';
import { MessageMenu, type MenuAction } from './MessageMenu.js';
import { Skeleton } from './Skeleton.js';

interface MessageListProps {
  channelId: string;
  /** Who is in the channel, so a sent message's ticks can be worked out. */
  memberIds: readonly string[];
  transcript: Transcript;
  pending: readonly PendingMessage[];
  /** Group channels label every message; a 1:1 does not need to. */
  showAuthors: boolean;
}

/** Consecutive messages from one person inside this window render as one block. */
const GROUP_WINDOW_MS = 5 * 60 * 1000;

/** How close to the bottom still counts as "following the conversation". */
const BOTTOM_SLACK_PX = 80;

/** An edit or delete with no answer by now is reported as failed. */
const ACK_TIMEOUT_MS = 10_000;

interface Row {
  key: string;
  message: Message | null;
  entry: PendingMessage | null;
  mine: boolean;
  senderId: string;
  createdAt: string;
  /** First of a run: gets the avatar, the author name and the tail corner. */
  first: boolean;
  /** Last of a run: gets the timestamp and delivery state. */
  last: boolean;
  dayHeading: string | null;
}

interface MenuState {
  x: number;
  y: number;
  actions: MenuAction[];
  /** Set for a stored message: those can carry reactions. */
  message: Message | null;
}

interface Flash {
  tone: 'ok' | 'error';
  text: string;
}

export function MessageList({
  channelId,
  memberIds,
  transcript,
  pending,
  showAuthors,
}: MessageListProps) {
  const currentUserId = useAuthStore((state) => state.user?.id ?? null);
  const loadOlder = useChatStore((state) => state.loadOlder);
  const receipts = useChatStore((state) => state.receipts[channelId] ?? EMPTY_RECEIPTS);
  const allowEditing = useSettingsStore((state) => state.settings.allowMessageEditing);
  const allowDeletion = useSettingsStore((state) => state.settings.allowMessageDeletion);
  const setReplyingTo = useChatStore((state) => state.setReplyingTo);
  // A string, so the selector is stable.
  const savedChannelId = useChatStore(
    (state) => state.channels.find((channel) => channel.type === 'saved')?.id ?? null,
  );
  const { socket } = useSocket();
  const [forwarding, setForwarding] = useState<Message | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  // A ref, not state: the scroll handler and the append effect both read it, and
  // neither should cause a render just because the user scrolled a pixel.
  const followingRef = useRef(true);
  const [following, setFollowing] = useState(true);
  const [missed, setMissed] = useState(0);

  const [menu, setMenu] = useState<MenuState | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Message | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);

  // One entry per distinct sender, plus the members the "Seen by" tooltip names.
  const nameIds = useMemo(
    () => [
      ...new Set([
        ...transcript.items.flatMap((message) => [
          message.senderId,
          ...(message.forwardedFrom ? [message.forwardedFrom] : []),
          ...(message.replyTo ? [message.replyTo.senderId] : []),
          ...message.reactions.flatMap((reaction) => reaction.userIds),
        ]),
        ...memberIds,
      ]),
    ],
    [transcript.items, memberIds],
  );
  const directory = useDirectory(nameIds);

  const rows = useMemo(
    () => buildRows(transcript.items, pending, currentUserId),
    [transcript.items, pending, currentUserId],
  );

  useEffect(() => {
    if (!flash) {
      return;
    }

    const timer = setTimeout(() => setFlash(null), 2_500);
    return () => clearTimeout(timer);
  }, [flash]);

  const closeMenu = useCallback(() => setMenu(null), []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior): void => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    element.scrollTo({ top: element.scrollHeight, behavior });
  }, []);

  const handleScroll = useCallback((): void => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
    const atBottom = distance <= BOTTOM_SLACK_PX;

    followingRef.current = atBottom;
    setFollowing(atBottom);

    if (atBottom) {
      setMissed(0);
    }
  }, []);

  const count = rows.length;
  const lastKey = rows[count - 1]?.key ?? '';
  const previousCountRef = useRef(0);
  const readyRef = useRef(false);
  const ready = transcript.status === 'ready';

  // Land at the bottom when a conversation first opens, without animating a
  // whole page of history past the reader.
  useEffect(() => {
    if (!ready || readyRef.current) {
      return;
    }

    readyRef.current = true;
    previousCountRef.current = count;
    followingRef.current = true;
    setFollowing(true);
    scrollToBottom('auto');
  }, [ready, count, scrollToBottom]);

  // Follow new messages only while the reader is already at the bottom. Older
  // pages are PREPENDED, which shortens nothing and changes no trailing key, so
  // loading history never yanks the view downwards.
  useEffect(() => {
    if (!readyRef.current) {
      return;
    }

    const added = count - previousCountRef.current;
    previousCountRef.current = count;

    if (added <= 0) {
      return;
    }

    if (followingRef.current) {
      scrollToBottom('smooth');
    } else {
      setMissed((current) => current + added);
    }
  }, [lastKey, count, scrollToBottom]);

  /**
   * Replaces the browser's context menu on a message with Copy / Edit / Delete.
   *
   * Shift + right-click is left alone, so the native menu (spell-check, "search
   * for...", extensions) is still one modifier away. A keyboard-opened menu
   * (the Menu key, Shift+F10) arrives at 0,0 and is anchored to the bubble.
   */
  function openMenu(
    event: MouseEvent<HTMLElement>,
    content: string,
    message: Message | null,
  ): void {
    if (event.shiftKey) {
      return;
    }

    event.preventDefault();

    const bubble = event.currentTarget;
    const selection = window.getSelection();
    const selected =
      selection && !selection.isCollapsed && bubble.contains(selection.anchorNode)
        ? selection.toString()
        : '';

    const actions: MenuAction[] = [];

    if (selected) {
      actions.push({
        id: 'copy-selection',
        label: 'Copy selection',
        icon: 'copy',
        onSelect: () => void copy(selected),
      });
    }

    if (message) {
      actions.push({
        id: 'reply',
        label: 'Reply',
        icon: 'reply',
        onSelect: () => setReplyingTo(channelId, message),
      });
    }

    actions.push({
      id: 'copy',
      label: message?.type === 'file-share-event' ? 'Copy file name' : 'Copy text',
      icon: 'copy',
      onSelect: () => void copy(content),
    });

    if (message) {
      actions.push({
        id: 'forward',
        label: 'Forward',
        icon: 'forward',
        onSelect: () => setForwarding(message),
      });
    }

    if (message && savedChannelId && savedChannelId !== channelId) {
      actions.push({
        id: 'save',
        label: 'Save',
        icon: 'bookmark',
        onSelect: () => void saveMessage(message, savedChannelId),
      });
    }

    const mine = message !== null && message.senderId === currentUserId;

    if (mine && message.type === 'text' && allowEditing) {
      actions.push({
        id: 'edit',
        label: 'Edit',
        icon: 'pencil',
        onSelect: () => setEditingId(message.id),
      });
    }

    if (mine && allowDeletion) {
      actions.push({
        id: 'delete',
        label: 'Delete',
        icon: 'trash',
        danger: true,
        onSelect: () => {
          setDeleteError(null);
          setDeleting(message);
        },
      });
    }

    const keyboard = event.clientX === 0 && event.clientY === 0;
    const rect = bubble.getBoundingClientRect();

    setMenu({
      x: keyboard ? rect.left : event.clientX,
      y: keyboard ? rect.bottom : event.clientY,
      actions,
      message,
    });
  }

  /** Copies a message into Saved messages - a forward with one fixed target. */
  async function saveMessage(message: Message, savedId: string): Promise<void> {
    if (!socket) {
      return;
    }

    try {
      const result = await socket.timeout(ACK_TIMEOUT_MS).emitWithAck('chat.forward', {
        channelId,
        messageId: message.id,
        targetChannelIds: [savedId],
      });

      setFlash(
        result.ok
          ? { tone: 'ok', text: 'Saved to Saved messages' }
          : { tone: 'error', text: result.message },
      );
    } catch {
      setFlash({ tone: 'error', text: 'Not saved' });
    }
  }

  /** Sets the caller's reaction, or removes it when it is the one they already have. */
  async function react(message: Message, emoji: string): Promise<void> {
    if (!socket || !currentUserId) {
      return;
    }

    const current = myReaction(message.reactions, currentUserId);

    try {
      const result = await socket.timeout(ACK_TIMEOUT_MS).emitWithAck('chat.react', {
        channelId,
        messageId: message.id,
        emoji: current === emoji ? null : emoji,
      });

      if (!result.ok) {
        setFlash({ tone: 'error', text: result.message });
      }
    } catch {
      setFlash({ tone: 'error', text: 'Reaction not saved' });
    }
  }

  async function copy(text: string): Promise<void> {
    const ok = await copyText(text);
    setFlash(ok ? { tone: 'ok', text: 'Copied' } : { tone: 'error', text: 'Could not copy' });
  }

  async function confirmDelete(): Promise<void> {
    if (!deleting || !socket) {
      return;
    }

    setDeleteBusy(true);

    try {
      const result = await socket
        .timeout(ACK_TIMEOUT_MS)
        .emitWithAck('chat.delete', { channelId, messageId: deleting.id });

      if (result.ok) {
        // The broadcast that removes it has already arrived: the server emits
        // to the room before it answers, and the socket keeps order.
        setDeleting(null);
      } else {
        setDeleteError(result.message);
      }
    } catch {
      setDeleteError('No answer from the server. Try again.');
    } finally {
      setDeleteBusy(false);
    }
  }

  if (transcript.status === 'loading' || transcript.status === 'idle') {
    return <TranscriptSkeleton />;
  }

  if (transcript.status === 'error') {
    return (
      <div className="transcript-state">
        <EmptyState
          icon="alertCircle"
          title="That conversation could not be loaded"
          description={transcript.error ?? undefined}
        />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="transcript-state">
        <EmptyState
          icon="messageSquare"
          title="No messages yet"
          description="Say hello. Messages are delivered in real time - and you can drop a file anywhere here to share it."
        />
      </div>
    );
  }

  return (
    <div className="transcript-wrap">
      <div className="transcript" ref={scrollRef} onScroll={handleScroll}>
        {transcript.hasMore ? (
          <div className="transcript-top">
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={transcript.loadingOlder}
              onClick={() => void loadOlder(channelId)}
            >
              {transcript.loadingOlder ? (
                <span className="btn-spinner" />
              ) : (
                <Icon name="clock" size={13} />
              )}
              {transcript.loadingOlder ? 'Loading…' : 'Load older messages'}
            </button>
          </div>
        ) : (
          <p className="transcript-start">Start of the conversation</p>
        )}

        {rows.map((row) =>
          row.message ? (
            <MessageRow
              key={row.key}
              row={row}
              message={row.message}
              showAuthors={showAuthors}
              currentUserId={currentUserId}
              directory={directory}
              memberIds={memberIds}
              receipts={receipts}
              editing={editingId === row.message.id}
              onCloseEditor={() => setEditingId(null)}
              onContextMenu={openMenu}
              onReact={(message, emoji) => void react(message, emoji)}
            />
          ) : row.entry ? (
            <PendingRow
              key={row.key}
              row={row}
              entry={row.entry}
              onContextMenu={openMenu}
              directory={directory}
              currentUserId={currentUserId}
            />
          ) : null,
        )}
      </div>

      {flash ? (
        <div className={`transcript-flash transcript-flash--${flash.tone}`} role="status">
          <Icon name={flash.tone === 'ok' ? 'check' : 'alertCircle'} size={13} />
          {flash.text}
        </div>
      ) : null}

      {following ? null : (
        <button
          type="button"
          className={missed > 0 ? 'jump-latest jump-latest--unread' : 'jump-latest'}
          onClick={() => {
            scrollToBottom('smooth');
            followingRef.current = true;
            setFollowing(true);
            setMissed(0);
          }}
        >
          <Icon name="arrowDown" size={14} />
          {missed > 0 ? `${missed} new message${missed === 1 ? '' : 's'}` : 'Jump to latest'}
        </button>
      )}

      {menu ? (
        <MessageMenu
          x={menu.x}
          y={menu.y}
          actions={menu.actions}
          onClose={closeMenu}
          reactions={
            menu.message && currentUserId
              ? {
                  current: myReaction(menu.message.reactions, currentUserId),
                  onReact: (emoji) => {
                    if (menu.message) {
                      void react(menu.message, emoji);
                    }
                  },
                }
              : undefined
          }
        />
      ) : null}

      {forwarding ? (
        <ForwardDialog
          message={forwarding}
          onDone={(count) => {
            setForwarding(null);

            if (count > 0) {
              setFlash({
                tone: 'ok',
                text: `Forwarded to ${count} conversation${count === 1 ? '' : 's'}`,
              });
            }
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          title="Delete message?"
          confirmLabel="Delete for everyone"
          busy={deleteBusy}
          error={deleteError}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setDeleting(null)}
        >
          <p>This removes the message for everyone in the conversation. It cannot be undone.</p>
          <blockquote className="confirm-quote" dir={dirFor(deleting.content)}>
            {deleting.content}
          </blockquote>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

/**
 * Flattens messages and unconfirmed sends into render rows, deciding day
 * headings and run boundaries once rather than in every row component.
 */
function buildRows(
  items: readonly Message[],
  pending: readonly PendingMessage[],
  currentUserId: string | null,
): Row[] {
  const rows: Row[] = [];

  for (const message of items) {
    rows.push({
      key: message.id,
      message,
      entry: null,
      mine: currentUserId !== null && message.senderId === currentUserId,
      senderId: message.senderId,
      createdAt: message.createdAt,
      first: true,
      last: true,
      dayHeading: null,
    });
  }

  for (const entry of pending) {
    rows.push({
      key: entry.tempId,
      message: null,
      entry,
      mine: true,
      senderId: currentUserId ?? 'me',
      createdAt: entry.sentAt,
      first: true,
      last: true,
      dayHeading: null,
    });
  }

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];

    if (!row) {
      continue;
    }

    const previous = rows[index - 1];
    const next = rows[index + 1];

    const newDay = !previous || !sameDay(previous.createdAt, row.createdAt);
    row.dayHeading = newDay ? formatDay(row.createdAt) : null;

    // A failed send breaks a run: it carries its own error and actions, and
    // should not read as part of the block above it.
    const runsWith = (other: Row | undefined): boolean =>
      other !== undefined &&
      other.senderId === row.senderId &&
      other.entry?.status !== 'failed' &&
      row.entry?.status !== 'failed' &&
      sameDay(other.createdAt, row.createdAt) &&
      Math.abs(new Date(other.createdAt).getTime() - new Date(row.createdAt).getTime()) <
        GROUP_WINDOW_MS;

    row.first = newDay || !runsWith(previous);
    row.last = !next || !sameDay(next.createdAt, row.createdAt) || !runsWith(next);
  }

  return rows;
}

function rowClasses(row: Row): string {
  const classes = ['msg'];

  if (row.mine) {
    classes.push('msg--mine');
  }

  classes.push(row.first ? 'msg--first' : 'msg--grouped');

  return classes.join(' ');
}

type OpenMenu = (event: MouseEvent<HTMLElement>, content: string, message: Message | null) => void;

function myReaction(reactions: readonly Reaction[], userId: string): string | null {
  return reactions.find((reaction) => reaction.userIds.includes(userId))?.emoji ?? null;
}

/** Brings a message into view and flashes it - the target of a reply quote. */
function revealMessage(messageId: string): void {
  const element = document.getElementById(`msg-${messageId}`);

  if (!element) {
    return;
  }

  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  element.classList.remove('msg--flash');
  // Reflow, so re-adding the class restarts the animation.
  void element.offsetWidth;
  element.classList.add('msg--flash');
}

/** The quoted message above a reply. */
function ReplyQuote({
  reply,
  currentUserId,
  directory,
}: {
  reply: MessageReply;
  currentUserId: string | null;
  directory: Record<string, PublicUser>;
}) {
  return (
    <button
      type="button"
      className="reply-quote"
      onClick={(event) => {
        event.stopPropagation();
        revealMessage(reply.id);
      }}
    >
      <span className="reply-quote-author">
        {memberLabel(reply.senderId, currentUserId, directory)}
      </span>
      <span className="reply-quote-text truncate" dir={dirFor(reply.content)}>
        {reply.type === 'file-share-event' ? `📎 ${reply.content}` : reply.content}
      </span>
    </button>
  );
}

/** Reaction chips under a bubble: the emoji, a count, yours highlighted. Click to toggle. */
function Reactions({
  reactions,
  currentUserId,
  directory,
  onToggle,
}: {
  reactions: readonly Reaction[];
  currentUserId: string | null;
  directory: Record<string, PublicUser>;
  onToggle: (emoji: string) => void;
}) {
  return (
    <div className="reactions">
      {reactions.map((reaction) => {
        const mine = currentUserId !== null && reaction.userIds.includes(currentUserId);
        const names = reaction.userIds.map((id) => memberLabel(id, currentUserId, directory));

        return (
          <button
            key={reaction.emoji}
            type="button"
            className={mine ? 'reaction reaction--mine' : 'reaction'}
            title={names.join(', ')}
            aria-label={`${reaction.emoji} ${reaction.userIds.length}: ${names.join(', ')}`}
            aria-pressed={mine}
            onClick={() => onToggle(reaction.emoji)}
          >
            <span className="reaction-emoji">{reaction.emoji}</span>
            {reaction.userIds.length <= 3 && reaction.userIds.length > 0 ? (
              <span className="reaction-faces">
                {reaction.userIds.map((id) => (
                  <Avatar
                    key={id}
                    name={memberLabel(id, currentUserId, directory)}
                    id={id}
                    size="xs"
                  />
                ))}
              </span>
            ) : (
              <span className="reaction-count">{reaction.userIds.length}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function MessageRow({
  row,
  message,
  showAuthors,
  currentUserId,
  directory,
  memberIds,
  receipts,
  editing,
  onCloseEditor,
  onContextMenu,
  onReact,
}: {
  row: Row;
  message: Message;
  showAuthors: boolean;
  currentUserId: string | null;
  directory: Record<string, PublicUser>;
  memberIds: readonly string[];
  receipts: Record<string, ChannelReceipt>;
  editing: boolean;
  onCloseEditor: () => void;
  onContextMenu: OpenMenu;
  onReact: (message: Message, emoji: string) => void;
}) {
  const author = memberLabel(message.senderId, currentUserId, directory);
  const isFile = message.type === 'file-share-event';

  return (
    <>
      {row.dayHeading ? (
        <div className="day-separator">
          <span>{row.dayHeading}</span>
        </div>
      ) : null}

      <article className={rowClasses(row)} id={`msg-${message.id}`}>
        {/* Reserved on the incoming side only, so a run of replies stays aligned
            under its avatar. Own messages hug the right edge with no gutter. */}
        {row.mine ? null : (
          <span className="msg-gutter">
            {row.first ? <Avatar name={author} id={message.senderId} size="sm" /> : null}
          </span>
        )}

        <div className={editing ? 'msg-body msg-body--editing' : 'msg-body'}>
          {row.first && showAuthors && !row.mine ? (
            <div className="msg-meta">
              <span className="msg-author" dir="auto">
                {author}
              </span>
            </div>
          ) : null}

          {editing ? (
            <MessageEditor message={message} onDone={onCloseEditor} />
          ) : (
            <div
              className={`bubble${isFile ? ' bubble--file' : row.mine ? ' bubble--mine' : ''}`}
              // The DOMINANT script decides, so a Persian message that opens with
              // a Latin word still reads right-to-left - see `chat/direction.ts`.
              dir={dirFor(message.content)}
              // Focusable, so the keyboard's Menu key reaches the same menu.
              tabIndex={0}
              onContextMenu={(event) => onContextMenu(event, message.content, message)}
            >
              {message.forwardedFrom ? (
                <span className="bubble-forwarded">
                  <Icon name="forward" size={12} />
                  Forwarded from {memberLabel(message.forwardedFrom, currentUserId, directory)}
                </span>
              ) : null}
              {message.replyTo ? (
                <ReplyQuote
                  reply={message.replyTo}
                  currentUserId={currentUserId}
                  directory={directory}
                />
              ) : null}
              {isFile && message.attachment ? (
                <FileCard attachment={message.attachment} />
              ) : (
                message.content
              )}
              {message.editedAt ? (
                <span className="bubble-edited" title={`Edited ${formatTime(message.editedAt)}`}>
                  edited
                </span>
              ) : null}
            </div>
          )}

          {message.reactions.length > 0 && !editing ? (
            <Reactions
              reactions={message.reactions}
              currentUserId={currentUserId}
              directory={directory}
              onToggle={(emoji) => onReact(message, emoji)}
            />
          ) : null}

          {row.last && !editing ? (
            <div className="msg-receipt">
              <time dateTime={message.createdAt}>{formatTime(message.createdAt)}</time>
              {/* Saved messages has nobody else to deliver to: no ticks. */}
              {row.mine && memberIds.length > 1 ? (
                <ReceiptTicks
                  message={message}
                  memberIds={memberIds}
                  receipts={receipts}
                  directory={directory}
                  currentUserId={currentUserId}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </article>
    </>
  );
}

/**
 * One tick for sent, two for delivered, two in the "seen" colour for read.
 * The label says the same in words, and in a group names who has read it.
 */
function ReceiptTicks({
  message,
  memberIds,
  receipts,
  directory,
  currentUserId,
}: {
  message: Message;
  memberIds: readonly string[];
  receipts: Record<string, ChannelReceipt>;
  directory: Record<string, PublicUser>;
  currentUserId: string | null;
}) {
  const status = deliveryStatus(message, memberIds, receipts);

  const label =
    status.state === 'seen'
      ? memberIds.length > 2
        ? `Seen by ${status.seenBy.map((id) => memberLabel(id, currentUserId, directory)).join(', ')}`
        : 'Seen'
      : status.state === 'delivered'
        ? 'Delivered'
        : 'Sent';

  return (
    <span className={`ticks ticks--${status.state}`} title={label}>
      <Icon
        name={status.state === 'sent' ? 'check' : 'checkCheck'}
        size={14}
        role="img"
        aria-label={label}
        aria-hidden={undefined}
      />
    </span>
  );
}

/**
 * A message shown immediately on send, before the server has confirmed it.
 *
 * Dimmed while in flight, so it is visibly distinct from a stored message rather
 * than pretending to be one. Only Copy is offered: there is nothing stored yet to
 * edit or delete.
 */
function PendingRow({
  row,
  entry,
  onContextMenu,
  directory,
  currentUserId,
}: {
  row: Row;
  entry: PendingMessage;
  onContextMenu: OpenMenu;
  directory: Record<string, PublicUser>;
  currentUserId: string | null;
}) {
  const { socket } = useSocket();
  const retryFailed = useChatStore((state) => state.retryFailed);
  const discardFailed = useChatStore((state) => state.discardFailed);

  const failed = entry.status === 'failed';

  return (
    <>
      {row.dayHeading ? (
        <div className="day-separator">
          <span>{row.dayHeading}</span>
        </div>
      ) : null}

      <article className={rowClasses(row)}>
        <div className="msg-body">
          <div
            className={`bubble ${failed ? 'bubble--failed' : 'bubble--mine bubble--pending'}`}
            dir={dirFor(entry.content)}
            tabIndex={0}
            onContextMenu={(event) => onContextMenu(event, entry.content, null)}
          >
            {entry.replyTo ? (
              <ReplyQuote
                reply={entry.replyTo}
                currentUserId={currentUserId}
                directory={directory}
              />
            ) : null}
            {entry.content}
          </div>

          <div className={failed ? 'msg-receipt msg-receipt--failed' : 'msg-receipt'}>
            <time dateTime={entry.sentAt}>{formatTime(entry.sentAt)}</time>
            {failed ? (
              <>
                <Icon name="alertCircle" size={13} />
                Not sent
              </>
            ) : (
              <>
                <Icon name="clock" size={13} />
                Sending
              </>
            )}
          </div>

          {failed ? (
            <div className="msg-actions">
              <span className="field-error">{entry.error}</span>
              {socket ? (
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => retryFailed(socket, entry.channelId, entry.tempId)}
                >
                  <Icon name="rotate" size={12} />
                  Retry
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => discardFailed(entry.channelId, entry.tempId)}
              >
                Discard
              </button>
            </div>
          ) : null}
        </div>
      </article>
    </>
  );
}

/** Placeholder bubbles while history loads, so the layout does not jump. */
function TranscriptSkeleton() {
  const widths = [52, 38, 64, 30, 46, 58];

  return (
    <div className="transcript-skeleton" aria-busy="true" aria-label="Loading messages">
      {widths.map((width, index) => {
        const mine = index % 3 === 2;

        return (
          <div className={mine ? 'skeleton-msg skeleton-msg--mine' : 'skeleton-msg'} key={width}>
            {mine ? null : <Skeleton width={28} height={28} circle />}
            <Skeleton width={`${width}%`} height={34} radius="var(--radius-md)" />
          </div>
        );
      })}
    </div>
  );
}
